import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import { COUNTRY_DIAL_CODES } from '../../data/countryDialCodes';
import { createAnnouncementSubscription } from '../../services/api';
import './NotifyMeForm.css';

// This audience is overwhelmingly Turkey-market — same default the phone
// field in ContactInfoModal.js already uses. See P16 for a proper
// geo/locale-based default; this is the same sensible-static choice made
// elsewhere until that lands.
const DEFAULT_DIAL_CODE = COUNTRY_DIAL_CODES.find((c) => c.name === 'Turkey')?.dialCode || '+90';

/** "Notify me when this opens" — shown only for an 'upcoming' announcement.
 * Stores intent only: creating a subscription does not send anything (see
 * the backend AnnouncementSubscriptionViewSet docstring). */
export default function NotifyMeForm({ announcement }) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState('email');
  const [email, setEmail] = useState('');
  const [dialCode, setDialCode] = useState(DEFAULT_DIAL_CODE);
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  if (announcement.status !== 'upcoming') return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (!consent) {
      setError('Please confirm you agree to be contacted before continuing.');
      return;
    }
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    if (method === 'email' && !trimmedEmail) {
      setError('Enter your email address.');
      return;
    }
    if (method === 'whatsapp' && !trimmedPhone) {
      setError('Enter your WhatsApp number.');
      return;
    }

    setSubmitting(true);
    createAnnouncementSubscription({
      announcement: announcement.id,
      email: method === 'email' ? trimmedEmail : '',
      whatsapp: method === 'whatsapp' ? `${dialCode} ${trimmedPhone}` : '',
    })
      .then(() => setSuccess(true))
      .catch((err) => setError(err.message))
      .finally(() => setSubmitting(false));
  };

  if (success) {
    return (
      <p className="notify-me-success">
        <Check size={14} aria-hidden="true" />
        You're set — we'll let you know when "{announcement.title}" opens.
      </p>
    );
  }

  if (!open) {
    return (
      <button type="button" className="notify-me-trigger" onClick={() => setOpen(true)}>
        <Bell size={14} aria-hidden="true" /> Notify me when this opens
      </button>
    );
  }

  return (
    <form className="notify-me-form" onSubmit={handleSubmit}>
      <div className="notify-me-method" role="group" aria-label="Contact method">
        <button
          type="button"
          className={method === 'email' ? 'active' : ''}
          aria-pressed={method === 'email'}
          onClick={() => setMethod('email')}
        >
          Email
        </button>
        <button
          type="button"
          className={method === 'whatsapp' ? 'active' : ''}
          aria-pressed={method === 'whatsapp'}
          onClick={() => setMethod('whatsapp')}
        >
          WhatsApp
        </button>
      </div>

      {method === 'email' ? (
        <input
          type="email"
          className="notify-me-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          aria-label="Email address"
          autoFocus
        />
      ) : (
        <div className="notify-me-phone-row">
          <select
            className="notify-me-dial-code"
            value={dialCode}
            onChange={(e) => setDialCode(e.target.value)}
            aria-label="Country code"
          >
            {COUNTRY_DIAL_CODES.map((c) => (
              <option key={c.cca2} value={c.dialCode}>{c.dialCode} {c.name}</option>
            ))}
          </select>
          <input
            type="tel"
            className="notify-me-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="552 811 7433"
            aria-label="WhatsApp number"
            autoFocus
          />
        </div>
      )}

      <label className="notify-me-consent">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>
          I agree to be contacted about this announcement only. See our{' '}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
        </span>
      </label>

      {error && <p className="notify-me-error">{error}</p>}

      <div className="notify-me-actions">
        <button type="submit" className="notify-me-submit" disabled={submitting}>
          {submitting ? 'Sending…' : 'Notify me'}
        </button>
        <button type="button" className="notify-me-cancel" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}
